// Team emblems (contracts/phase3.md "Teams": `{ shape: 1–6, palette: tile recipe name }`,
// drawn as on boards E2/E3). Pure TypeScript, no React: the web OG renderer embeds
// `emblemSvg()` as an image; `./react` draws the same geometry in the DOM.
//
// Shapes follow the E2 picker's geometric emblems, one distinct outline each:
//   1 hex (the default; E3's "hex emblem"), 2 circle, 3 triangle, 4 diamond, 5 pentagon, 6 octagon.
// E2 shows the hex twice (teal and violet); the contract separates shape from palette,
// so the sixth slot is an octagon here. Colours come from the tile recipe (see
// palette.generated.ts); the E2 picker offers teal, violet, pink, azure, ember and moss.

import { TILE_EMBLEM_COLORS } from "./palette.generated";

export { TILE_EMBLEM_COLORS };

export type TilePalette = keyof typeof TILE_EMBLEM_COLORS;
export type EmblemShape = 1 | 2 | 3 | 4 | 5 | 6;

export interface Emblem {
  shape: EmblemShape;
  palette: TilePalette;
}

export interface EmblemColors {
  /** The fill (the recipe's key colour); also the glow. */
  key: string;
  /** The top-left highlight of the fill gradient. */
  light: string;
  /** Initials drawn on the emblem. */
  ink: string;
}

export const SHAPE_NAMES = ["hex", "circle", "triangle", "diamond", "pentagon", "octagon"] as const;
export type ShapeName = (typeof SHAPE_NAMES)[number];

/** The E2 picker's palettes, in order. Any tile recipe name is accepted. */
export const EMBLEM_PALETTES = ["teal", "violet", "pink", "azure", "ember", "moss"] as const satisfies readonly TilePalette[];

export const TILE_PALETTES = Object.keys(TILE_EMBLEM_COLORS) as TilePalette[];

/** Outline points in a 100×100 box (the boards' clip-path polygons); the circle is a special case. */
const POLYGONS: Record<ShapeName, readonly (readonly [number, number])[] | null> = {
  hex: [[50, 0], [100, 25], [100, 75], [50, 100], [0, 75], [0, 25]],
  circle: null,
  triangle: [[50, 0], [100, 100], [0, 100]],
  diamond: [[50, 0], [100, 50], [50, 100], [0, 50]],
  pentagon: [[50, 0], [100, 38], [82, 100], [18, 100], [0, 38]],
  octagon: [[29.3, 0], [70.7, 0], [100, 29.3], [100, 70.7], [70.7, 100], [29.3, 100], [0, 70.7], [0, 29.3]],
};

/** Where initials sit (visual centre) and how large they may be, per shape, in the 100×100 box. */
const TEXT_BOX: Record<ShapeName, { cy: number; size: number }> = {
  hex: { cy: 51, size: 30 },
  circle: { cy: 51, size: 31 },
  triangle: { cy: 68, size: 22 },
  diamond: { cy: 51, size: 24 },
  pentagon: { cy: 60, size: 27 },
  octagon: { cy: 51, size: 31 },
};

export const shapeName = (shape: EmblemShape): ShapeName => SHAPE_NAMES[shape - 1]!;

/** SVG path data for a shape in a 100×100 box. */
export function emblemPath(shape: EmblemShape): string {
  const points = POLYGONS[shapeName(shape)];
  if (!points) return "M50 0A50 50 0 1 1 50 100A50 50 0 1 1 50 0Z";
  return `M${points.map(([x, y]) => `${x} ${y}`).join("L")}Z`;
}

export const emblemTextBox = (shape: EmblemShape) => TEXT_BOX[shapeName(shape)];

export function emblemColors(palette: TilePalette): EmblemColors {
  return TILE_EMBLEM_COLORS[palette] ?? TILE_EMBLEM_COLORS.teal;
}

function hash(s: string): number {
  let h = 2166136261;
  for (const ch of s) h = Math.imul(h ^ ch.codePointAt(0)!, 16777619);
  return h >>> 0;
}

const PALETTE_ALIASES: Record<string, TilePalette> = {
  mint: "teal",
  purple: "violet",
  blue: "azure",
  sky: "azure",
  orange: "ember",
  amber: "ember",
  lime: "moss",
  yellow: "moss",
  green: "moss",
  red: "rose",
};

const camel = (s: string) => s.trim().replace(/[-_\s]+(\w)/g, (_, c: string) => c.toUpperCase());

function resolvePalette(raw: unknown): TilePalette | null {
  if (typeof raw !== "string") return null;
  const name = camel(raw.replace(/^tile[-.]/i, ""));
  const exact = TILE_PALETTES.find((p) => p.toLowerCase() === name.toLowerCase());
  return exact ?? PALETTE_ALIASES[name.toLowerCase()] ?? null;
}

function resolveShape(raw: unknown): EmblemShape | null {
  if (typeof raw === "number" && Number.isInteger(raw) && raw >= 1 && raw <= 6) return raw as EmblemShape;
  if (typeof raw === "string") {
    const s = raw.trim().toLowerCase();
    if (/^[1-6]$/.test(s)) return Number(s) as EmblemShape;
    const i = SHAPE_NAMES.indexOf(s as ShapeName);
    if (i >= 0) return (i + 1) as EmblemShape;
  }
  return null;
}

/**
 * The API's emblem → `{ shape, palette }`. Accepts `{ shape: 1–6 | "hex", palette: "violet" | "tile-violet" }`,
 * or a bare palette string. Anything missing falls back to the hex and a stable pick from the E2 palettes
 * (seeded by the team name or code), so an emblem always draws.
 */
export function normaliseEmblem(raw: unknown, seed = ""): Emblem {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const palette = resolvePalette(typeof raw === "string" ? raw : (o.palette ?? o.color ?? o.colour)) ?? EMBLEM_PALETTES[hash(seed) % EMBLEM_PALETTES.length]!;
  const shape = resolveShape(o.shape) ?? 1;
  return { shape, palette };
}

/** "Stackbrain" → "ST", "Hall 4 Goblins" → "HG", "the night shift" → "NS". Two letters at most. */
export function teamInitials(name: string): string {
  const words = name
    .normalize("NFKD")
    .replace(/[^\p{L}\p{N}\s-]/gu, "")
    .split(/[\s-]+/)
    .filter(Boolean);
  const meaningful = words.filter((w) => !/^(the|team|a|an|of|and)$/i.test(w) && !/^\d+$/.test(w));
  const pick = meaningful.length ? meaningful : words;
  if (!pick.length) return "";
  if (pick.length === 1) {
    // CamelCase ("StackBrain") gives two capitals; otherwise the first two letters.
    const caps = pick[0]!.match(/\p{Lu}/gu);
    if (caps && caps.length >= 2) return (caps[0]! + caps[1]!).toUpperCase();
    return [...pick[0]!].slice(0, 2).join("").toUpperCase();
  }
  return ([...pick[0]!][0]! + [...pick[1]!][0]!).toUpperCase();
}

export interface EmblemSvgOptions {
  /** Rendered size of the shape in px (the SVG adds room for the glow when `glow` is on). */
  size: number;
  /** Initials on the emblem (omit on small sizes; the boards drop them below ~40px). */
  initials?: string;
  /** Soft bloom in the key colour (E3: drop-shadow 0 0 20px at 60%). */
  glow?: boolean;
  /** Font family for initials. */
  fontFamily?: string;
  /** Unique suffix for gradient / filter ids when several SVGs share a document. */
  idSuffix?: string;
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Glow padding around the shape, as a fraction of its size. */
export const GLOW_PAD = 0.3;
/** Glow blur in the 100×100 shape box (the filter runs in the scaled path's user space). */
export const GLOW_BLUR = 11;

/** The emblem as a standalone SVG string (OG images, `<img>`). */
export function emblemSvg(emblem: Emblem, opts: EmblemSvgOptions): { svg: string; width: number; height: number } {
  const { size, initials, glow = false, fontFamily = "Outfit, system-ui, sans-serif", idSuffix = "" } = opts;
  const c = emblemColors(emblem.palette);
  const pad = glow ? Math.round(size * GLOW_PAD) : 0;
  const w = size + pad * 2;
  const s = size / 100;
  const t = `translate(${pad} ${pad}) scale(${s})`;
  const path = emblemPath(emblem.shape);
  const box = emblemTextBox(emblem.shape);
  const g = `eg${idSuffix}`;
  const f = `eb${idSuffix}`;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${w}" viewBox="0 0 ${w} ${w}">` +
    `<defs>` +
    `<radialGradient id="${g}" gradientUnits="userSpaceOnUse" cx="34" cy="26" r="72"><stop offset="0" stop-color="${c.light}"/><stop offset="0.62" stop-color="${c.key}"/><stop offset="1" stop-color="${c.key}"/></radialGradient>` +
    (glow ? `<filter id="${f}" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="${GLOW_BLUR}"/></filter>` : "") +
    `</defs>` +
    (glow ? `<path d="${path}" transform="${t}" fill="${c.key}" opacity="0.6" filter="url(#${f})"/>` : "") +
    `<path d="${path}" transform="${t}" fill="url(#${g})"/>` +
    (initials
      ? `<text x="50" y="${box.cy}" transform="${t}" text-anchor="middle" dominant-baseline="central" font-family="${esc(fontFamily)}" font-weight="700" font-size="${box.size}" fill="${c.ink}">${esc(initials)}</text>`
      : "") +
    `</svg>`;
  return { svg, width: w, height: w };
}

// ---------------------------------------------------------------- division and rank labels (shared copy)

/** `{ tier: 2 }` → "Division 2" (tier 1 is the top; boards E3/E4 name divisions by tier). */
export const divisionLabel = (tier: number) => `Division ${tier}`;

/** 1 → "1st", 2 → "2nd", 13 → "13th". */
export function ordinal(n: number): string {
  const v = Math.abs(n) % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][v % 10] ?? "th"}`;
}
